// "Ekstra øvelser" — synthetic 1-round trins triggered from the dashboard.
// Same pattern as `buildWarmupTrin`: a synthetic trin with a special id
// (prefixed `__`) so the curriculum's progression machinery doesn't try
// to mark it as completed.

import {
  NIVEAU_1_SPRINT_WORDS,
  NIVEAU_1_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-1';
import {
  NIVEAU_2_SPRINT_WORDS,
  NIVEAU_2_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-2';
import {
  NIVEAU_3_SPRINT_WORDS,
  NIVEAU_3_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-3';
import {
  NIVEAU_4_ALPHABET_WORDS,
  NIVEAU_4_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-4';
import {
  NIVEAU_5_SPRINT_WORDS,
  NIVEAU_5_UNLOCKED_KEYS,
} from '../data/curriculum/niveau-5';
import { NIVEAU_6_UNLOCKED_KEYS } from '../data/curriculum/niveau-6';
import { fromChunks, interleaved, literal, round } from '../data/curriculum/generators';
import {
  getCumulativeUnlockedKeys,
  joinDictationUnits,
  pickDictationUnits,
} from './dictationUnits';
import { pickStory2Seed } from '../data/story2/seeds';
import { pickStoryForUser } from './story';
import { defaultPreferences } from './storage';
import {
  effectiveMetronomeBpmForUser,
  shadowCpsForUser,
} from './presentationTempo';
import type { Trin, UserProfile } from '../types';

export const METRONOME_TRIN_ID = '__metronome__';
export const SHADOW_TRIN_ID = '__shadow__';
export const DICTATION_TRIN_ID = '__dictation__';
export const STORY_TRIN_ID = '__story__';
export const STORY2_TRIN_ID = '__story2__';
export const BONUS_TAL_TRIN_ID = '__bonus_tal__';
export const BONUS_EMAIL_TRIN_ID = '__bonus_email__';

const DIGIT_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

interface TierAsset {
  chunks: string[];
  unlockedKeys: string[];
}

const TIER_ASSETS: Record<string, TierAsset> = {
  'niveau-1': {
    chunks: NIVEAU_1_SPRINT_WORDS,
    unlockedKeys: NIVEAU_1_UNLOCKED_KEYS,
  },
  'niveau-2': {
    chunks: NIVEAU_2_SPRINT_WORDS,
    unlockedKeys: NIVEAU_2_UNLOCKED_KEYS,
  },
  'niveau-3': {
    chunks: NIVEAU_3_SPRINT_WORDS,
    unlockedKeys: NIVEAU_3_UNLOCKED_KEYS,
  },
  'niveau-4': {
    chunks: NIVEAU_4_ALPHABET_WORDS,
    unlockedKeys: NIVEAU_4_UNLOCKED_KEYS,
  },
  'niveau-5': {
    chunks: NIVEAU_5_SPRINT_WORDS,
    unlockedKeys: NIVEAU_5_UNLOCKED_KEYS,
  },
};

const TIER_ORDER = [
  'niveau-1',
  'niveau-2',
  'niveau-3',
  'niveau-4',
  'niveau-5',
] as const;

function highestClearedAsset(user: UserProfile): TierAsset | null {
  for (let i = TIER_ORDER.length - 1; i >= 0; i--) {
    if (user.completedTierIds.includes(TIER_ORDER[i])) {
      return TIER_ASSETS[TIER_ORDER[i]] ?? null;
    }
  }
  return null;
}

/** True once user has finished at least niveau-1 — metronome from level 2+. */
export function isMetronomeEligible(user: UserProfile): boolean {
  return user.completedTierIds.includes('niveau-1');
}

/** Shadow round is harder; require niveau-2 completed. */
export function isShadowEligible(user: UserProfile): boolean {
  return user.completedTierIds.includes('niveau-2');
}

/** Orddiktat — available once niveau-1 is cleared (same as metronome). */
export function isDictationEligible(user: UserProfile): boolean {
  return user.completedTierIds.includes('niveau-1');
}

/** Fortælling — interaktivt diktat; efter niveau 3 (næsten hele alfabetet). */
export function isStoryEligible(user: UserProfile): boolean {
  return user.completedTierIds.includes('niveau-3');
}

/** Fortælling 2 — AI-afskrift; samme unlock som Fortælling 1. */
export function isStory2Eligible(user: UserProfile): boolean {
  return isStoryEligible(user);
}

/** Bonus: tal — after niveau 6. */
export function isBonusTalEligible(user: UserProfile): boolean {
  return user.completedTierIds.includes('niveau-6');
}

/** Bonus: @ og e-mail — after niveau 6. */
export function isBonusEmailEligible(user: UserProfile): boolean {
  return user.completedTierIds.includes('niveau-6');
}

export function buildBonusTalTrin(user: UserProfile): Trin | null {
  if (!isBonusTalEligible(user)) return null;
  const unlockedKeys = [...DIGIT_KEYS, ' '];

  return {
    id: BONUS_TAL_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Bonus: Tal',
    description: 'Lær tallene på øverste række — til datoer og mængder.',
    goal: 'Ram 0–9 uden at kigge på tastaturet.',
    unlockedKeys,
    newKeys: ['1', '2'],
    strictCase: true,
    rounds: [
      round({
        id: `${BONUS_TAL_TRIN_ID}-r1`,
        title: '1 og 2',
        purpose: 'Lillefinger og ringfinger venstre.',
        mode: 'guided',
        generate: literal('1 2 1 2 12 21 11 22 12 21'),
      }),
      round({
        id: `${BONUS_TAL_TRIN_ID}-r2`,
        title: '3 og 4',
        purpose: 'Lang- og pegefinger venstre.',
        mode: 'guided',
        generate: literal('3 4 3 4 34 43 13 24 34 43'),
      }),
      round({
        id: `${BONUS_TAL_TRIN_ID}-r3`,
        title: '5 og 6',
        purpose: 'Pegefingre begge sider.',
        mode: 'guided',
        generate: literal('5 6 5 6 56 65 15 26 56 65'),
      }),
      round({
        id: `${BONUS_TAL_TRIN_ID}-r4`,
        title: '7 og 8',
        purpose: 'Pege- og ringfinger højre.',
        mode: 'recall',
        generate: literal('7 8 7 8 78 87 17 28 78 87'),
      }),
      round({
        id: `${BONUS_TAL_TRIN_ID}-r5`,
        title: '9 og 0',
        purpose: 'Ring- og lillefinger højre.',
        mode: 'recall',
        generate: literal('9 0 9 0 90 09 19 20 90 09'),
      }),
      round({
        id: `${BONUS_TAL_TRIN_ID}-r6`,
        title: 'Blandet',
        purpose: 'Tal i hverdagskontekst.',
        mode: 'challenge',
        generate: literal('12 elever 34 timer 56 sider 78 ord 90 procent'),
      }),
    ],
  };
}

export function buildBonusEmailTrin(user: UserProfile): Trin | null {
  if (!isBonusEmailEligible(user)) return null;
  const unlockedKeys = [...NIVEAU_6_UNLOCKED_KEYS, '@'];

  return {
    id: BONUS_EMAIL_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Bonus: @ og e-mail',
    description: 'Skriv @ og simple e-mailadresser med Alt/Option.',
    goal: 'Find @ på dit tastatur og skriv en kort adresse.',
    unlockedKeys,
    newKeys: ['@'],
    strictCase: true,
    allowAltLayer: true,
    rounds: [
      round({
        id: `${BONUS_EMAIL_TRIN_ID}-r1`,
        title: '@-tegnet',
        purpose: 'Øv @ alene.',
        mode: 'guided',
        generate: literal('@ @ @ @ @ @'),
      }),
      round({
        id: `${BONUS_EMAIL_TRIN_ID}-r2`,
        title: 'Brugernavn',
        purpose: 'Navn før @.',
        mode: 'guided',
        generate: literal('lærke@ gitte@ freja@ maria@'),
      }),
      round({
        id: `${BONUS_EMAIL_TRIN_ID}-r3`,
        title: 'E-mailadresse',
        purpose: 'Komplet adresse.',
        mode: 'recall',
        generate: literal('lærke@skole.dk gitte@skole.dk'),
      }),
      round({
        id: `${BONUS_EMAIL_TRIN_ID}-r4`,
        title: 'Sætning',
        purpose: 'E-mail i en kort sætning.',
        mode: 'recall',
        generate: literal('Min mail er lærke@skole.dk'),
      }),
      round({
        id: `${BONUS_EMAIL_TRIN_ID}-r5`,
        title: 'Udfordring',
        purpose: 'Uden hjælp.',
        mode: 'challenge',
        generate: literal('Skriv til gitte@skole.dk i dag.'),
      }),
    ],
  };
}

export function buildMetronomeTrin(user: UserProfile): Trin | null {
  const asset = highestClearedAsset(user);
  if (!asset) return null;
  const bpm = effectiveMetronomeBpmForUser(user);

  return {
    id: METRONOME_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Rytme-runde',
    description: 'Skriv én tast pr. tik. Lærer fingrene en jævn rytme.',
    goal: 'Hold tempoet stabilt — under 100 ms afvigelse pr. tast.',
    unlockedKeys: asset.unlockedKeys,
    newKeys: [],
    rounds: [
      round({
        id: `${METRONOME_TRIN_ID}-r1`,
        title: `Metronom ${bpm} BPM`,
        purpose: 'Ramt rytme er hurtigere end ramt fart.',
        mode: 'recall',
        generate: fromChunks(asset.chunks, 90),
        presentation: { kind: 'metronome', bpm },
      }),
    ],
  };
}

function interleavedSourcesForUser(
  user: UserProfile,
): Array<{ chunks: string[]; weight: number }> {
  const sources: Array<{ chunks: string[]; weight: number }> = [];
  for (const tier of TIER_ORDER) {
    if (user.completedTierIds.includes(tier)) {
      const a = TIER_ASSETS[tier];
      sources.push({ chunks: a.chunks, weight: 1 });
    }
  }
  return sources;
}

export function buildStory2Trin(user: UserProfile): Trin | null {
  if (!isStory2Eligible(user)) return null;

  const lastId = user.preferences?.lastStory2SeedId;
  const seed = pickStory2Seed(lastId);
  const unlockedKeys = getCumulativeUnlockedKeys(user);

  return {
    id: STORY2_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Fortælling 2',
    description: `${seed.title} — skriv historien på skærmen og find på ord undervejs.`,
    goal: 'Skab en ny historie sammen med fortælleren — afskrift og egne ord i flow.',
    unlockedKeys,
    newKeys: [],
    rounds: [
      round({
        id: `${STORY2_TRIN_ID}-r1`,
        title: seed.title,
        purpose: 'Læs og skriv sætningerne — udfyld huller når de kommer.',
        mode: 'recall',
        generate: () => seed.id,
        presentation: { kind: 'story2', seedId: seed.id },
      }),
    ],
  };
}

export function buildStoryTrin(user: UserProfile): Trin | null {
  if (!isStoryEligible(user)) return null;

  const script = pickStoryForUser(user);
  if (!script) return null;

  const unlockedKeys = getCumulativeUnlockedKeys(user);

  return {
    id: STORY_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Fortælling',
    description: `${script.title} — lyt og udfyld hullerne med dine egne ord.`,
    goal: 'Leg med en lille historie — kun hullerne skrives, resten oplæses.',
    unlockedKeys,
    newKeys: [],
    rounds: [
      round({
        id: `${STORY_TRIN_ID}-r1`,
        title: script.title,
        purpose: 'Lyt til sætningen og skriv kun ordene i hullerne.',
        mode: 'recall',
        generate: () => script.id,
        presentation: { kind: 'story', storyId: script.id },
      }),
    ],
  };
}

export function buildDictationTrin(user: UserProfile): Trin | null {
  if (!isDictationEligible(user)) return null;

  const unitLength =
    user.preferences?.dictationUnitLength ??
    defaultPreferences().dictationUnitLength ??
    1;
  const lexicon = user.preferences?.dictationLexicon === 'en' ? 'en' : 'da';
  const units = pickDictationUnits(user, unitLength);
  if (units.length === 0) return null;

  const unlockedKeys = getCumulativeUnlockedKeys(user);
  const sessionText = joinDictationUnits(units);
  const wordLabel =
    lexicon === 'en'
      ? unitLength >= 7
        ? '6+ words'
        : unitLength === 1
          ? '1 word'
          : `${unitLength} words`
      : unitLength >= 7
        ? '6+ ord'
        : `${unitLength} ord`;

  return {
    id: DICTATION_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Orddiktat',
    description:
      lexicon === 'en'
        ? `Listen to ${wordLabel} at a time and type them without seeing the text.`
        : `Lyt til ${wordLabel} ad gangen og skriv dem uden at se teksten.`,
    goal:
      lexicon === 'en'
        ? 'Train your ear and fingers — meaningful units.'
        : 'Træn øret og fingrene — meningsfulde enheder.',
    unlockedKeys,
    newKeys: [],
    rounds: [
      round({
        id: `${DICTATION_TRIN_ID}-r1`,
        title: `Orddiktat · ${wordLabel}`,
        purpose:
          lexicon === 'en'
            ? 'Listen to the whole unit, type word by word.'
            : 'Lyt til hele enheden, skriv ord for ord.',
        mode: 'recall',
        generate: literal(sessionText),
        presentation: { kind: 'dictation', dictationMode: 'unit' },
      }),
    ],
  };
}

export function buildShadowTrin(user: UserProfile): Trin | null {
  const asset = highestClearedAsset(user);
  if (!asset) return null;

  const sources = interleavedSourcesForUser(user);
  if (sources.length === 0) return null;
  const cps = shadowCpsForUser(user);

  return {
    id: SHADOW_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Skygge-runde',
    description:
      'Teksten ruller af sig selv. Følg med — ingen tid til at tænke.',
    goal: 'Hold tempoet uden at falde bagud.',
    unlockedKeys: asset.unlockedKeys,
    newKeys: [],
    rounds: [
      round({
        id: `${SHADOW_TRIN_ID}-r1`,
        title: `Auto-scroll · ${cps} t/s`,
        purpose: 'Tving jævnt tempo.',
        mode: 'challenge',
        generate: interleaved(sources, 100),
        presentation: { kind: 'shadow', cps },
      }),
    ],
  };
}

/** Any of the synthetic non-curriculum trin ids. */
export function isExtraTrinId(id: string): boolean {
  return id.startsWith('__') && id.endsWith('__');
}

export {
  buildWeakSpotsTrin,
  analyzeWeakSpots,
  formatWeakSpotKeysLabel,
  isWeakSpotsEligible,
  WEAK_SPOTS_TRIN_ID,
} from './weakSpots';

export {
  buildPunctuationPracticeTrin,
  isPunctuationPracticeEligible,
  PUNCTUATION_PRACTICE_TRIN_ID,
} from './punctuationPractice';

export {
  buildCustomPracticeShellTrin,
  buildCustomPracticeTrin,
  isCustomPracticeEligible,
  CUSTOM_PRACTICE_TRIN_ID,
} from './customPractice';

export {
  buildMentalImageryTrin,
  isMentalImageryEligible,
  mentalImageryWordPool,
  MENTAL_IMAGERY_TRIN_ID,
  MENTAL_IMAGERY_WORD_COUNT,
  MENTAL_IMAGERY_MAX_WORD_LENGTH,
  MENTAL_IMAGERY_MAX_REPEATS,
  MENTAL_IMAGERY_MIN_REPEATS,
  clampMentalImageryRepeats,
} from './mentalImagery';
